import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Plus, Share2, Eye, Trash2, Link2, Copy, Check, ExternalLink, Database, Pencil
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_STYLES = {
  draft: 'bg-zinc-100 text-zinc-600', shared: 'bg-emerald-50 text-emerald-700', viewed: 'bg-blue-50 text-blue-700',
};

export default function ProposalsPage() {
  const navigate = useNavigate();
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);

  const fetchProposals = async () => {
    try { const r = await api.get('/proposals'); setProposals(r.data); }
    catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchProposals(); }, []);

  const toggleShare = async (id) => {
    try {
      const r = await api.put(`/proposals/${id}/share`);
      setProposals(prev => prev.map(p => p.proposal_id === id ? { ...p, status: r.data.status } : p));
      toast.success(r.data.status === 'shared' ? 'Proposal shared' : 'Proposal unshared');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const deleteProposal = async (id) => {
    if (!window.confirm('Delete this proposal?')) return;
    try { await api.delete(`/proposals/${id}`); toast.success('Deleted'); fetchProposals(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const copyLink = (token) => {
    const url = `${window.location.origin}/p/${token}`;
    navigator.clipboard.writeText(url);
    setCopiedId(token);
    toast.success('Link copied');
    setTimeout(() => setCopiedId(null), 2000);
  };

  if (loading) {
    return (<div className="min-h-screen bg-white"><Navbar /><div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-zinc-300 border-t-zinc-900 rounded-full animate-spin" /></div></div>);
  }

  return (
    <div className="min-h-screen bg-white" data-testid="proposals-page">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="flex items-end justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-[#0A0A0A]" style={{ fontFamily: 'Chivo, sans-serif' }}>Proposals</h1>
            <p className="text-sm text-[#4B5563] mt-1">{proposals.length} proposals created</p>
          </div>
          <Button onClick={() => navigate('/proposals/new')} className="rounded-none bg-[#0A0A0A] text-white hover:bg-[#171717]" data-testid="create-proposal-btn">
            <Plus className="w-4 h-4 mr-1.5" /> New Proposal
          </Button>
        </div>

        {proposals.length === 0 ? (
          <div className="border border-dashed border-[#E5E7EB] py-16 text-center" data-testid="no-proposals">
            <Share2 className="w-8 h-8 text-[#9CA3AF] mx-auto mb-3" />
            <p className="text-sm text-[#4B5563]">No proposals yet</p>
            <p className="text-xs text-[#9CA3AF] mt-1">Create one to share schema elements with external stakeholders</p>
          </div>
        ) : (
          <div className="border border-[#E5E7EB]" data-testid="proposals-list">
            {proposals.map(p => (
              <div key={p.proposal_id} className="flex items-center gap-4 px-4 py-3 border-b border-[#E5E7EB] last:border-b-0 hover:bg-[#F9FAFB] transition-colors" data-testid={`proposal-${p.proposal_id}`}>
                <div className="flex-1 min-w-0">
                  <button onClick={() => navigate(`/proposals/${p.proposal_id}`)} className="text-sm font-semibold text-[#0A0A0A] hover:text-[#002FA7] text-left" data-testid={`proposal-title-${p.proposal_id}`}>
                    {p.title}
                  </button>
                  <div className="flex items-center gap-2 mt-1">
                    <Database className="w-3 h-3 text-[#9CA3AF]" strokeWidth={1.5} />
                    <span className="text-xs text-[#4B5563]">{p.schema_name}</span>
                    <span className="text-[10px] text-[#9CA3AF]">{p.attribute_count} attributes</span>
                    <span className="text-[10px] text-[#9CA3AF]">by {p.created_by_name}</span>
                  </div>
                </div>
                <Badge className={`text-[10px] px-2 py-0.5 rounded-none font-semibold ${STATUS_STYLES[p.status] || STATUS_STYLES.draft}`}>
                  {p.status}
                </Badge>
                {p.view_count > 0 && (
                  <span className="text-[10px] text-[#9CA3AF] flex items-center gap-1"><Eye className="w-3 h-3" /> {p.view_count}</span>
                )}
                {p.status === 'shared' && (
                  <Button variant="outline" size="sm" className="h-7 rounded-none text-[10px]" onClick={() => copyLink(p.share_token)} data-testid={`copy-link-${p.proposal_id}`}>
                    {copiedId === p.share_token ? <Check className="w-3 h-3 mr-1 text-emerald-600" /> : <Copy className="w-3 h-3 mr-1" />}
                    {copiedId === p.share_token ? 'Copied' : 'Copy Link'}
                  </Button>
                )}
                <Button variant="ghost" size="sm" className="h-7 rounded-none text-[10px] text-[#4B5563]" onClick={() => navigate(`/proposals/${p.proposal_id}/edit`)} data-testid={`edit-${p.proposal_id}`}>
                  <Pencil className="w-3 h-3 mr-1" /> Edit
                </Button>
                <Button variant="ghost" size="sm" className={`h-7 rounded-none text-[10px] ${p.status === 'shared' ? 'text-amber-600' : 'text-emerald-600'}`} onClick={() => toggleShare(p.proposal_id)} data-testid={`share-${p.proposal_id}`}>
                  <Share2 className="w-3 h-3 mr-1" /> {p.status === 'shared' ? 'Unshare' : 'Share'}
                </Button>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 rounded-none text-red-500" onClick={() => deleteProposal(p.proposal_id)} data-testid={`delete-proposal-${p.proposal_id}`}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
